"use client";
import  { useState, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { Pagination, A11y, FreeMode, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/scss/free-mode";
import "swiper/scss";
import { CarouselContainer } from "./styles";
import { CarouselProps } from "./types";
import {
  NextButton,
  PreviousButton,
} from "../buttons/ControlButtons";
import {
  stopPropagation,
} from "@itchannelplus/utils";

export const Carousel = ({
  items,
  renderItem,
  children,
  showCTRLButtons = false,
  slidesPerView = "auto",
  spaceBetween = 30,
  pagination = true,
  freeMode = false,
  breakpoints,
  centeredSlides,
  autoplay,
  loop,
  allowTouchMove = false,
  autoHide = false,
  centerInsufficientSlides = false,
  CTRLButtonsPosition = "inside",
  btnTheme,
  autoHeight = false,
  ...props
}: CarouselProps) => {
  const [swiper, setSwiper] = useState<any>();
  const [showCtrl, setShowCtrl] = useState<boolean>(showCTRLButtons);
  const [previousDisabled, setPreviousDisabled] = useState<any>(false);
  const [nextDisabled, setNextDisabled] = useState<any>(false);
  const { ref: containerRef, inView } = useInView({ threshold: 0.25 });
  const prevInView = useRef(inView);

  const modules = [A11y];
  if (freeMode) modules.push(FreeMode);
  if (pagination) modules.push(Pagination);
  if (autoplay) modules.push(Autoplay);
  const handlePrev = stopPropagation(() => {
    swiper && swiper?.slidePrev();
  });

  const handleNext = stopPropagation(() => {
    swiper && swiper?.slideNext();
  });
  const shouldShowControls = function (instance:any) {
    if (instance.virtualSize > instance.size) setShowCtrl(showCTRLButtons);
    else setShowCtrl(false);
  };

  const handleSwiper = (swiperInstance: any) => {
    setSwiper(swiperInstance);
    swiperInstance.on("resize", shouldShowControls);
    setPreviousDisabled(swiperInstance.isBeginning);
    setNextDisabled(swiperInstance.isEnd);
    swiperInstance.on("activeIndexChange", () => {
      setPreviousDisabled(swiperInstance.isBeginning);
      setNextDisabled(swiperInstance.isEnd);
    });
  };
  //centerInsufficientSlides={true}
  // autoplay only when in viewport
  useEffect(() => {
    if (!autoplay || !swiper) return;
    if (inView && !prevInView.current) {
      swiper.autoplay.start();
    } else if (!inView && prevInView.current) {
      swiper.autoplay.stop();
    }
    prevInView.current = inView;
  }, [inView, autoplay, swiper]);

  return (
    <CarouselContainer {...props} ctrlButtons={CTRLButtonsPosition} ref={containerRef}>
      <Swiper
        onSwiper={handleSwiper}
        onInit={shouldShowControls}
        onResize={shouldShowControls}
        onLock={() => setShowCtrl(false)}
        onUnlock={() => setShowCtrl(showCTRLButtons)}
        autoplay={autoplay}
        centerInsufficientSlides={centerInsufficientSlides}
        watchOverflow={showCTRLButtons && autoHide}
        loop={loop}
        modules={modules}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        freeMode={freeMode}
        breakpoints={breakpoints}
        centeredSlides={centeredSlides}
        pagination={pagination ? { clickable: true } : false}
        rewind={showCTRLButtons}
        allowTouchMove={allowTouchMove || !showCTRLButtons}
        autoHeight={autoHeight}
      >
        {items && renderItem &&
          items.map((item, index) => (
            <SwiperSlide key={"slide" + index}>{renderItem(item)}</SwiperSlide>
          ))}
        {children &&
          Array.isArray(children) &&
          children.map((child, index) => (
            <SwiperSlide key={"slide" + index}>{child}</SwiperSlide>
          ))}
        {children && !Array.isArray(children) && (
          <SwiperSlide key={"slide"}>{children}</SwiperSlide>
        )}
      </Swiper>
      {showCtrl && (
        <>
          <PreviousButton
            onClick={handlePrev}
            theme={btnTheme || (CTRLButtonsPosition == "inside" ? "light" : "dark")}
            disabled={previousDisabled}
            autoHideMobile={autoHide}
            position={CTRLButtonsPosition}
          />
          <NextButton
            onClick={handleNext}
            theme={btnTheme || (CTRLButtonsPosition == "inside" ? "light" : "dark")}
            disabled={nextDisabled}
            autoHideMobile={autoHide}
            position={CTRLButtonsPosition}
          />
        </>
      )}
    </CarouselContainer>
  );
};
